import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  ArrowLeft, Search, Plus, Building2, MapPin, User, Phone, 
  Filter, RefreshCw, ChevronRight, Upload, Download, FileSpreadsheet,
  CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BuildersList = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [builders, setBuilders] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [cities, setCities] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // CSV Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCities();
    fetchBuilders();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchBuilders();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, cityFilter, statusFilter]);

  const fetchCities = async () => {
    try {
      const res = await api.getBDCities();
      setCities(res.data.cities || []);
    } catch (error) {
      console.error('Failed to load cities:', error);
    }
  };

  const fetchBuilders = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (cityFilter) params.city = cityFilter;
      if (statusFilter) params.status = statusFilter;
      
      const res = await api.getBuilders(params);
      setBuilders(res.data.builders || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to load builders:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have access to BD Desk');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  // CSV Import Functions
  const downloadTemplate = async () => {
    try {
      const res = await api.getBuilderImportTemplate();
      const template = res.data;
      
      // Create CSV content
      const headers = template.columns.map(c => c.name).join(',');
      const sampleRows = template.sample_rows.map(row => 
        template.columns.map(c => {
          const val = row[c.name] || '';
          return typeof val === 'string' && val.includes(',') ? `"${val}"` : val;
        }).join(',')
      ).join('\n');
      
      const csvContent = `${headers}\n${sampleRows}`;
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'builders_import_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const rows = parseCSV(text);
        
        if (rows.length === 0) {
          toast.error('No data found in CSV');
          return;
        }
        
        setImportData(rows);
        setImportResult(null);
        setShowImportModal(true);
      } catch (error) {
        toast.error('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    e.target.value = '';
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    const rows = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (const char of lines[i]) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      
      const row = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx] || '';
      });
      
      // Only add rows with at least builder_name
      if (row.builder_name) {
        rows.push(row);
      }
    }
    
    return rows;
  };

  const handleImport = async () => {
    if (!importData || importData.length === 0) return;
    
    setImporting(true);
    try {
      const res = await api.importBuildersCSV({ builders: importData });
      setImportResult(res.data);
      
      if (res.data.created > 0 || res.data.updated > 0) {
        toast.success(`Import completed: ${res.data.created} created, ${res.data.updated} updated`);
        fetchBuilders();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
      setImportResult({ error: error.response?.data?.detail || 'Import failed' });
    } finally {
      setImporting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active':
        return <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>;
      case 'Inactive':
        return <Badge className="bg-slate-100 text-slate-600">Inactive</Badge>;
      case 'Blacklisted':
        return <Badge className="bg-red-100 text-red-700">Blacklisted</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getCategoryBadge = (category) => {
    const colors = {
      'Affordable': 'bg-green-100 text-green-700',
      'Mid': 'bg-blue-100 text-blue-700',
      'Premium': 'bg-purple-100 text-purple-700',
      'Luxury': 'bg-amber-100 text-amber-700',
      'Launch': 'bg-pink-100 text-pink-700',
      'Auction': 'bg-orange-100 text-orange-700',
      'NRI': 'bg-cyan-100 text-cyan-700',
    };
    return category ? (
      <Badge className={colors[category] || 'bg-slate-100 text-slate-600'}>{category}</Badge>
    ) : null;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/bd')}
              >
                <ArrowLeft size={18} />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Builders</h1>
                <p className="text-sm text-slate-500">{total} builders</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".csv"
                className="hidden"
              />
              <Button variant="outline" onClick={downloadTemplate}>
                <Download size={16} className="mr-1" />
                Template
              </Button>
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                <Upload size={16} className="mr-1" />
                Import CSV
              </Button>
              <Button onClick={() => navigate('/bd/builders/new')}>
                <Plus size={16} className="mr-1" />
                Add Builder
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* Search & Filters */}
        <div className="mb-4 space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search builders..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button 
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={16} className="mr-1" />
              Filters
            </Button>
          </div>

          {showFilters && (
            <div className="flex flex-wrap gap-2 p-3 bg-slate-100 rounded-lg">
              <Select value={cityFilter || "all"} onValueChange={(v) => setCityFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[150px] bg-white">
                  <SelectValue placeholder="All Cities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {cities.map(city => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[150px] bg-white">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                  <SelectItem value="Blacklisted">Blacklisted</SelectItem>
                </SelectContent>
              </Select>

              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setCityFilter('');
                  setStatusFilter('');
                }}
              >
                Clear
              </Button>
            </div>
          )}
        </div>

        {/* Builders List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin text-purple-600" size={32} />
          </div>
        ) : builders.length === 0 ? (
          <Card className="p-12 text-center">
            <Building2 size={48} className="mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700">No builders found</h3>
            <p className="text-slate-500 mt-1">
              {search ? 'Try a different search term' : 'Add your first builder to get started'}
            </p>
            <Button className="mt-4" onClick={() => navigate('/bd/builders/new')}>
              <Plus size={16} className="mr-1" />
              Add Builder
            </Button>
          </Card>
        ) : (
          <div className="grid gap-3">
            {builders.map((builder) => (
              <Card 
                key={builder.builder_id}
                className="p-4 bg-white hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/bd/builders/${builder.builder_id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-800">{builder.builder_name}</h3>
                      {getStatusBadge(builder.status)}
                      {getCategoryBadge(builder.category)}
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                      {builder.city && (
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {builder.city}
                        </span>
                      )}
                      {builder.spoc_name && (
                        <span className="flex items-center gap-1">
                          <User size={14} />
                          {builder.spoc_name}
                        </span>
                      )}
                      {builder.spoc_contact && (
                        <span className="flex items-center gap-1">
                          <Phone size={14} />
                          {builder.spoc_contact}
                        </span>
                      )}
                    </div>

                    <div className="flex gap-4 mt-2 text-xs text-slate-400">
                      <span>{builder.project_count || 0} projects</span>
                      {builder.assigned_bd_owner_name && (
                        <span>BD: {builder.assigned_bd_owner_name}</span>
                      )}
                    </div>
                  </div>
                  
                  <ChevronRight size={20} className="text-slate-400" />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* CSV Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet size={20} />
              Import Builders from CSV
            </DialogTitle>
            <DialogDescription>
              Review the data below before importing
            </DialogDescription>
          </DialogHeader>

          {!importResult ? (
            <>
              {/* Preview Data */}
              <div className="mt-4">
                <div className="text-sm font-medium text-slate-700 mb-2">
                  {importData?.length || 0} rows to import:
                </div>
                <div className="border rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">#</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Builder Name</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">City</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Category</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">SPOC</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {importData?.slice(0, 50).map((row, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                          <td className="px-3 py-2 font-medium">{row.builder_name}</td>
                          <td className="px-3 py-2">{row.city || '-'}</td>
                          <td className="px-3 py-2">{row.category || '-'}</td>
                          <td className="px-3 py-2">{row.spoc_name || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importData?.length > 50 && (
                    <div className="px-3 py-2 bg-slate-50 text-sm text-slate-500">
                      ... and {importData.length - 50} more rows
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setShowImportModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleImport} disabled={importing}>
                  {importing ? (
                    <>
                      <RefreshCw size={16} className="mr-1 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload size={16} className="mr-1" />
                      Import {importData?.length || 0} Builders
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Import Results */}
              <div className="mt-4 space-y-4">
                {importResult.error ? (
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center gap-2 text-red-700 font-medium">
                      <XCircle size={18} />
                      Import Failed
                    </div>
                    <p className="text-sm text-red-600 mt-1">{importResult.error}</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 text-center">
                        <div className="flex items-center justify-center gap-1 text-emerald-700">
                          <CheckCircle size={18} />
                          <span className="text-2xl font-bold">{importResult.created}</span>
                        </div>
                        <div className="text-sm text-emerald-600">Created</div>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
                        <div className="flex items-center justify-center gap-1 text-blue-700">
                          <RefreshCw size={18} />
                          <span className="text-2xl font-bold">{importResult.updated}</span>
                        </div>
                        <div className="text-sm text-blue-600">Updated</div>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-center">
                        <div className="flex items-center justify-center gap-1 text-red-700">
                          <XCircle size={18} />
                          <span className="text-2xl font-bold">{importResult.total_failed}</span>
                        </div>
                        <div className="text-sm text-red-600">Failed</div>
                      </div>
                    </div>

                    {importResult.failed?.length > 0 && (
                      <div className="border rounded-lg p-3">
                        <div className="flex items-center gap-2 text-amber-700 font-medium mb-2">
                          <AlertCircle size={16} />
                          Failed Rows
                        </div>
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                          {importResult.failed.map((f, idx) => (
                            <div key={idx} className="text-sm p-2 bg-red-50 rounded">
                              <span className="font-medium">Row {f.row}:</span> {f.error}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="flex justify-end">
                  <Button onClick={() => {
                    setShowImportModal(false);
                    setImportData(null);
                    setImportResult(null);
                  }}>
                    Done
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BuildersList;
